import { WebManifestSchema } from '@contracts/schemas/pwa.schema';
import {
  appIconUrl,
  appIconVersion,
  setAppIconVersion,
  subscribeAppIcon,
} from '@/shared/branding/appLogo';
import { env } from '@/shared/config/env';

/**
 * The tab's icon and the iPhone Home Screen icon, from the upload in force.
 * Request of 6 October 2026: the logo saved in Settings is the favicon too.
 */
function applyIconLinks(): void {
  const version = appIconVersion();
  const tab = document.querySelector('link[rel="icon"]');
  tab?.setAttribute('href', version === null ? '/icon.svg' : appIconUrl(version, 'icon-192.png'));
  tab?.setAttribute('type', version === null ? 'image/svg+xml' : 'image/png');
  document
    .querySelector('link[rel="apple-touch-icon"]')
    ?.setAttribute(
      'href',
      version === null ? '/apple-touch-icon.png' : appIconUrl(version, 'apple-touch-icon.png'),
    );
}

let watching = false;

/**
 * The saved theme colour and icon set, on the open page. Request of 5 October
 * 2026.
 *
 * The manifest carries them for an install; the page itself reads two things
 * the manifest does not reach (and, since the request of 6 October 2026, the
 * tab icon and the logo on the sign-in card and in the menu): the theme-color meta, which colours the browser
 * bar and the status bar of an app installed before the change, and the
 * apple-touch-icon link, which is what an iPhone uses when added to the Home
 * Screen (iOS reads neither the manifest's icons nor its colours).
 *
 * Fire and forget: a page without them keeps the defaults in index.html.
 */
export async function applyAppBranding(): Promise<void> {
  if (!watching) {
    watching = true;
    // A later upload or reset in Settings reaches the links without a reload.
    subscribeAppIcon(applyIconLinks);
  }
  try {
    const response = await fetch(`${env.apiBaseUrl}/pwa/manifest.webmanifest`, {
      cache: 'no-store',
    });
    if (!response.ok) return;
    const manifest = WebManifestSchema.safeParse(await response.json());
    if (!manifest.success) return;

    document
      .querySelector('meta[name="theme-color"]')
      ?.setAttribute('content', manifest.data.theme_color);

    const uploaded = manifest.data.icons.find((icon) => icon.src.includes('/pwa/icons/'));
    const version = uploaded === undefined ? null : /\/pwa\/icons\/([^/]+)\//.exec(uploaded.src);
    setAppIconVersion(version?.[1] ?? null);
    applyIconLinks();
  } catch {
    // Offline, or the API is restarting: the defaults stand until the next load.
  }
}
